import React, {
  useState,
  createContext,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from 'react'
import { Plus, Minus } from 'lucide-react'
import RichText from '@/utils/RichText.js'

type AccordionVariant = 'default' | 'dark'

interface AccordionContextType {
  openIndex: number | null
  setOpenIndex: (index: number | null) => void
  getItemIndex: (id: string) => number
  variant: AccordionVariant
}

interface AccordionProps {
  children: React.ReactNode
  className?: string
  defaultOpenId?: string
  onToggle?: (id: string, isOpen: boolean) => void
  variant?: AccordionVariant
}

interface AccordionItemProps {
  children: React.ReactNode
  title: string
  id: string
  className?: string
  variant?: AccordionVariant
}

const AccordionContext = createContext<AccordionContextType | null>(null)

const useAccordion = () => {
  const context = useContext(AccordionContext)
  if (!context) {
    throw new Error('Accordion components must be used within an Accordion')
  }
  return context
}

const ACCORDION_VARIANT: Record<AccordionVariant, string> = {
  default: 'text-gray-900',
  dark: 'text-emerald-500',
}

const ACCORDION_VARIANT_ITEM: Record<AccordionVariant, string> = {
  default: 'text-gray-600',
  dark: 'text-white',
}

export const AccordionItem = ({
  children,
  title,
  id,
  className = '',
}: AccordionItemProps) => {
  const { openIndex, setOpenIndex, getItemIndex, variant } = useAccordion()
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number>(0)
  const index = getItemIndex(id)
  const isOpen = openIndex === index
  const variantClass = ACCORDION_VARIANT[variant]
  const variantItemClass = ACCORDION_VARIANT_ITEM[variant]

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (contentRef.current) {
          setHeight(contentRef.current.scrollHeight)
        }
      })

      resizeObserver.observe(contentRef.current)
      return () => resizeObserver.disconnect()
    }
  }, [])

  return (
    <div
      className={`border-b border-dotted border-emerald-500 ${className}`}
    >
      <button
        className="w-full space-x-6 py-6 flex justify-between items-center text-left"
        onClick={() => setOpenIndex(isOpen ? null : index)}
        type="button"
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${id}`}
      >
        <span className={`prose reduce-top-space font-medium text-xl  ${variantClass} not-prose`}><RichText>{title}</RichText></span>
        <span className="p-1 hover:rounded-full hover:bg-gray-100 transition-colors duration-200">
          {isOpen ? (
            <Minus
              className="w-6 h-6 text-emerald-500"
              aria-hidden="true"
              strokeWidth={4}
            />
          ) : (
            <Plus
              className="w-6 h-6 text-emerald-500"
              aria-hidden="true"
              strokeWidth={4}
            />
          )}
        </span>
      </button>

      <div
        id={`accordion-content-${id}`}
        role="region"
        aria-labelledby={`accordion-button-${id}`}
        style={{ height: isOpen ? `${height}px` : '0' }}
        className="transition-all duration-300 ease-out overflow-hidden"
      >
        <div ref={contentRef} className={`prose pt-3 pb-8 ${variantItemClass}`}>
          {children}
        </div>
      </div>
    </div>
  )
}

export const Accordion = ({
  children,
  className = '',
  defaultOpenId,
  onToggle,
  variant = 'default',
}: AccordionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(() => {
    if (defaultOpenId) {
      const childrenArray = React.Children.toArray(children)
      const index = childrenArray.findIndex(child => {
        if (React.isValidElement(child) && 'id' in (child as React.ReactElement<any>).props) {
          return (child as React.ReactElement<any>).props.id === defaultOpenId
        }
        return false
      })
      return index !== -1 ? index : null
    }
    return null
  })

  const getItemIndex = useCallback(
    (id: string): number => {
      const childrenArray = React.Children.toArray(children)
      return childrenArray.findIndex(child => {
        if (React.isValidElement(child) && 'id' in (child as React.ReactElement<any>).props) {
          return (child as React.ReactElement<any>).props.id === id
        }
        return false
      })
    },
    [children],
  )

  const handleSetOpenIndex = useCallback(
    (index: number | null) => {
      setOpenIndex(index)
      if (onToggle) {
        const childrenArray = React.Children.toArray(children)
        const child = childrenArray[index ?? -1] as React.ReactElement<any>
        if (React.isValidElement(child) && 'id' in (child as React.ReactElement<any>).props) {
          onToggle((child as React.ReactElement<any>).props.id, index !== null)
        }
      }
    },
    [children, onToggle],
  )

  return (
    <AccordionContext.Provider
      value={{
        openIndex,
        setOpenIndex: handleSetOpenIndex,
        getItemIndex,
        variant,
      }}
    >
      <div className={`w-full ${className}`}>
        {children}
      </div>
    </AccordionContext.Provider>
  )
}
